import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings; accept
// either string or non-negative number (matches AR / loan / project).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed counterpart — for net/derived figures that CAN be negative (a go-live
// tie-out variance, a closing−GL rollforward variance). DecimalLike is
// non-negative-only, so allow any finite decimal string or a coerced number here.
const SignedDecimalLike = z.union([
    z.string().refine((v) => {
        try {
            return Number.isFinite(Number(v)) && v.trim() !== '';
        }
        catch {
            return false;
        }
    }, 'Invalid signed decimal'),
    z.coerce.number(),
]);
// ============================================================
// PrepaidStatus enum (mirrors Prisma)
// ============================================================
export const PrepaidStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'FULLY_AMORTIZED', 'TERMINATED']);
// ============================================================
// PrepaidRunStatus enum (mirrors Prisma)
// ============================================================
export const PrepaidRunStatusSchema = z.enum(['PENDING_APPROVAL', 'POSTED', 'REVERSED']);
// ============================================================
// AmortizationMethod enum (mirrors Prisma)
// ============================================================
export const AmortizationMethodSchema = z.enum(['EVEN', 'PRORATE_FIRST_LAST']);
// ============================================================
// PrepaidSourceType enum (mirrors Prisma)
// ============================================================
export const PrepaidSourceTypeSchema = z.enum(['ACQUISITION', 'OPENING_BALANCE']);
// ============================================================
// PrepaidAmortizationEntry — child rows of a contract
// ============================================================
export const PrepaidAmortizationEntrySchema = z.object({
    id: z.uuid(),
    contractId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    amount: DecimalLike,
    remainingBalance: DecimalLike,
    journalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
});
// ============================================================
// PrepaidContract — header
// ============================================================
export const PrepaidContractSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1),
    description: z.string().min(1),
    vendorId: z.uuid().nullable().optional(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date(),
    totalAmount: DecimalLike,
    monthlyAmount: DecimalLike,
    amortizationMethod: AmortizationMethodSchema,
    sourceType: PrepaidSourceTypeSchema,
    expenseAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    status: PrepaidStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — adds derived display fields + amortized-so-far summary.
export const PrepaidContractListRowSchema = PrepaidContractSchema.extend({
    vendorName: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    monthsTotal: z.number().int().nonnegative(),
    monthsAmortized: z.number().int().nonnegative(),
    amortizedAmount: DecimalLike,
    remainingAmount: DecimalLike,
    // Evidence gate (§9.5). `requiresEvidence` = company opted in AND this contract
    // is still pre-first-run; `missingEvidenceCount` drives the amber register badge.
    attachmentCount: z.number().int().nonnegative().optional(),
    requiresEvidence: z.boolean().optional(),
    missingEvidenceCount: z.number().int().nonnegative().optional(),
});
// Detail — header + amortization entries + display names.
export const PrepaidContractWithEntriesSchema = PrepaidContractListRowSchema.extend({
    entries: z.array(PrepaidAmortizationEntrySchema),
});
// ============================================================
// POST /prepaid-contracts — create + acquisition JV
// ============================================================
export const CreatePrepaidContractSchema = z
    .object({
    description: z.string().min(1),
    vendorId: z.uuid().nullable().optional(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    totalAmount: z.coerce.number().positive(),
    expenseAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    // Cash/bank account on the Cr side of the acquisition JV. When omitted the
    // service resolves the per-company BANK_PRIMARY account — matches the
    // AR/AP convention.
    cashAccountCode: z.string().min(1).optional(),
    amortizationMethod: AmortizationMethodSchema.optional(),
})
    .refine((v) => new Date(v.startDate) < new Date(v.endDate), {
    message: 'startDate must be before endDate',
    path: ['endDate'],
});
// ============================================================
// POST /prepaid-contracts/opening-balance — go-live take-on
// ============================================================
// Go-live take-on of a mid-life prepaid: residual-only opening JV + backfilled history.
export const CreateOpeningBalanceSchema = z
    .object({
    description: z.string().min(1),
    vendorId: z.uuid().nullable().optional(),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    totalAmount: z.coerce.number().positive(),
    expenseAccountCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    amortizationMethod: AmortizationMethodSchema.optional(),
    asOfPeriod: z.string().regex(/^\d{6}$/),
    monthsAmortizedSoFar: z.coerce.number().int().nonnegative(),
})
    .refine((v) => new Date(v.startDate) < new Date(v.endDate), {
    message: 'startDate must be before endDate',
    path: ['endDate'],
});
// ============================================================
// POST /prepaid-contracts/amortize — batched monthly run
// ============================================================
export const AmortizePrepaidSchema = z
    .object({
    period: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    fromPeriod: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    toPeriod: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    excludeContractIds: z.array(z.uuid()).optional(),
})
    .refine((v) => v.period ?? v.fromPeriod, { message: 'period or fromPeriod is required' });
// ============================================================
// POST /prepaid-contracts/amortization-runs/:id/reverse — FB08-style unwind
// ============================================================
export const ReversePrepaidRunSchema = z.object({
    reason: z.string().trim().min(1, 'reason is required'),
});
export const ReversePrepaidRunResultSchema = z.object({
    runId: z.uuid(),
    status: z.literal('REVERSED'),
    reversalJournalEntryId: z.uuid().nullable(),
    reversalVoucherNo: z.string().nullable(),
    reversalPeriod: z.string().regex(/^\d{6}$/),
    entriesReversed: z.number().int().nonnegative(),
    contractsRestored: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
});
// ============================================================
// GET /prepaid-contracts/opening-balance-tieout — go-live reconciliation
// ============================================================
// Go-live migration tie-out: residuals brought on vs migrated remaining (variance 0 at cutover).
export const OpeningBalanceTieoutSchema = z.object({
    migratedContracts: z.number().int().nonnegative(),
    openingBalanceTotal: DecimalLike, // Σ opening-JV debits to PREPAID_ASSET
    migratedRemaining: DecimalLike, // Σ remaining of OPENING_BALANCE contracts
    variance: SignedDecimalLike, // openingBalanceTotal - migratedRemaining (signed)
});
// ============================================================
// PATCH /prepaid-contracts/:id — mutable header fields only
// ============================================================
// Mutable header fields only — financials/dates change via modify/re-spread (later phase), NOT here.
export const UpdatePrepaidContractSchema = z.object({
    description: z.string().min(1).optional(),
    vendorId: z.uuid().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
});
// ============================================================
// P6 lifecycle — terminate+write-off, modify/re-spread, suspend/resume, expense-direct
// ============================================================
// Terminate early: write-off the unamortized remaining (+ optional refund) and
// freeze the contract. effectiveDate's period must be OPEN.
export const TerminatePrepaidSchema = z.object({
    effectiveDate: z.iso.date(),
    reason: z.string().trim().min(1, 'reason is required'),
    refundAmount: z.coerce.number().nonnegative().optional(),
    refundAccountCode: z.string().min(1).optional(),
});
// Modify / re-spread: change term and/or total; posted tranches immutable; future
// re-spread over the remaining term; a total change posts a top-up/reduction JV.
export const ModifyPrepaidSchema = z
    .object({
    endDate: z.iso.date().optional(),
    totalAmount: z.coerce.number().positive().optional(),
    // Posting date for the top-up/reduction JV when totalAmount changes (its
    // period must be OPEN). Ignored for an end-date-only modify (no JV).
    effectiveDate: z.iso.date(),
    cashAccountCode: z.string().min(1).optional(),
    reason: z.string().trim().min(1, 'reason is required'),
})
    .refine((v) => v.endDate !== undefined || v.totalAmount !== undefined, {
    message: 'endDate or totalAmount must change',
    path: ['endDate'],
});
// Pause / resume.
export const SuspendPrepaidSchema = z.object({
    reason: z.string().trim().min(1, 'reason is required'),
});
// Materiality → expense directly (no contract, no schedule): Dr expense / Cr cash.
export const ExpenseDirectSchema = z
    .object({
    description: z.string().min(1),
    date: z.iso.date(),
    totalAmount: z.coerce.number().positive(),
    expenseAccountCode: z.string().min(1),
    cashAccountCode: z.string().min(1).optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
})
    .strict();
// Company prepaid settings (materiality threshold + auto-run + evidence gate).
export const PrepaidSettingsSchema = z.object({
    prepaidMaterialityThreshold: DecimalLike.nullable(),
    prepaidAutoRun: z.boolean(),
    prepaidEvidenceRequired: z.boolean(),
});
// ============================================================
// GET /:id/history — append-only contract change/lifecycle events (P7 §9.4).
// One row per modify (field diffs) / terminate / suspend / resume; reuses the
// ApprovalEvent table (detail Json carries { changes: [{field,before,after}], … }).
// ============================================================
export const PrepaidFieldChangeSchema = z.object({
    field: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
});
export const PrepaidHistoryEventSchema = z.object({
    id: z.uuid(),
    eventType: z.string(),
    actorName: z.string(),
    reason: z.string().nullable().optional(),
    detail: z.unknown().nullable().optional(),
    createdAt: z.coerce.date(),
});
// ============================================================
// Evidence attachments (P7 §9.5) — bound source docs (MinIO). storageKey is
// internal (presigned download goes through GET …/:attachmentId/url).
// ============================================================
export const PrepaidAttachmentSchema = z.object({
    id: z.uuid(),
    contractId: z.uuid(),
    kind: z.enum(['IMAGE', 'DOCUMENT']),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    sha256: z.string(),
    caption: z.string().nullable().optional(),
    uploadedBy: z.string().nullable().optional(),
    uploadedAt: z.coerce.date(),
});
// Response shape for the presigned-download endpoint.
export const PrepaidAttachmentDownloadUrlSchema = z.object({
    url: z.string(),
    expiresAt: z.coerce.date(),
});
// Response from POST /amortize — useful for the run modal "preview JV" UX.
export const AmortizeResultSchema = z.object({
    period: z.string(),
    journalEntryId: z.uuid().nullable(),
    journalVoucherNo: z.string().nullable(),
    contractsProcessed: z.number().int().nonnegative(),
    contractsSkipped: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
    entries: z.array(PrepaidAmortizationEntrySchema),
});
// Range result — one AmortizeResult per period in the requested range.
export const AmortizeRangeResultSchema = z.object({
    periods: z.array(AmortizeResultSchema),
    grandTotal: DecimalLike,
    warnings: z.array(z.string()),
});
// ============================================================
// Amortization RUN ENTITY (governed) — header + approval chain.
// Distinct from AmortizationRunRow (the JV-derived posting history): this is the
// first-class PrepaidAmortizationRun lifecycle doc the approval engine drives.
// ============================================================
export const PrepaidRunApprovalRowSchema = z.object({
    id: z.uuid(),
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
});
export const PrepaidRunEntitySchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    fromPeriod: z.string().regex(/^\d{6}$/),
    toPeriod: z.string().regex(/^\d{6}$/),
    status: PrepaidRunStatusSchema,
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().int(),
    totalAmount: DecimalLike,
    contractsProcessed: z.number().int().nonnegative(),
    batchJournalEntryId: z.uuid().nullable().optional(),
    reversalJournalEntryId: z.uuid().nullable().optional(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    reversedBy: z.string().nullable().optional(),
    reversedAt: z.coerce.date().nullable().optional(),
    reversalReason: z.string().nullable().optional(),
    isAuto: z.boolean(),
    createdAt: z.coerce.date(),
    approvals: z.array(PrepaidRunApprovalRowSchema),
});
// A single past amortization run (one batched JV covering N contracts in a period).
export const AmortizationRunRowSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    journalEntryId: z.uuid().nullable(),
    journalVoucherNo: z.string().nullable(),
    voucherDate: z.coerce.date().nullable(),
    contractsProcessed: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
    postedBy: z.string().nullable(),
    postedAt: z.coerce.date().nullable(),
});
// ============================================================
// POST /prepaid-contracts/amortize/preview — dry-run preview
// ============================================================
export const AmortizePreviewSchema = z.object({
    fromPeriod: z.string().regex(/^\d{6}$/),
    toPeriod: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    excludeContractIds: z.array(z.uuid()).optional(),
});
export const PreviewLineSchema = z.object({
    contractId: z.uuid(),
    contractCode: z.string(),
    description: z.string(),
    expenseAccountCode: z.string(),
    amount: DecimalLike,
    remainingBalance: DecimalLike,
    isFinal: z.boolean(),
});
export const PreviewPeriodSchema = z.object({
    period: z.string(),
    periodOpen: z.boolean(),
    lines: z.array(PreviewLineSchema),
    skipped: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
    warnings: z.array(z.string()),
});
export const AmortizePreviewResultSchema = z.object({
    periods: z.array(PreviewPeriodSchema),
    grandTotal: DecimalLike,
    contractsTotal: z.number().int().nonnegative(),
});
// ============================================================
// GET /prepaid-contracts/:id/schedule — full projected schedule
// (posted entries overlaid on buildAmortizationSchedule output)
// ============================================================
export const PrepaidScheduleRowSchema = z.object({
    index: z.number().int(),
    period: z.string(),
    voucherDate: z.coerce.date(),
    amount: DecimalLike,
    openingBalance: DecimalLike,
    closingBalance: DecimalLike,
    status: z.enum(['posted', 'current', 'pending']),
    journalEntryId: z.uuid().nullable(),
});
// ============================================================
// P8 — Roll-forward + GL reconciliation (§3.6).
// Per-contract Opening + Additions − Amortization − Write-off = Closing for a
// period; TOTAL closing diffed against the live PREPAID_ASSET (10190001) GL
// balance → variance (signed — can be negative).
// ============================================================
export const PrepaidRollforwardRowSchema = z.object({
    contractId: z.uuid(),
    code: z.string(),
    description: z.string(),
    vendorName: z.string().nullable(),
    branchName: z.string().nullable(),
    opening: DecimalLike,
    additions: DecimalLike,
    amortization: DecimalLike,
    writeOff: DecimalLike,
    closing: DecimalLike,
});
export const PrepaidRollforwardResultSchema = z.object({
    period: z.string(),
    rows: z.array(PrepaidRollforwardRowSchema),
    totals: z.object({
        opening: DecimalLike,
        additions: DecimalLike,
        amortization: DecimalLike,
        writeOff: DecimalLike,
        closing: DecimalLike,
    }),
    glBalance: SignedDecimalLike,
    variance: SignedDecimalLike,
});
// ============================================================
// P8 — Reconciliation exception worklist (§3.6).
// Categorized, deep-linkable breaks behind the red variance light.
// ============================================================
export const PrepaidReconExceptionCategorySchema = z.enum([
    'UNAMORTIZED_ACTIVE', // ACTIVE contract overlapping the period with no live entry
    'NULL_JV_ENTRY', // a posted-period live entry missing its journalEntryId (genuine un-posted)
    'BALANCE_MISMATCH', // totalAmount ≠ Σ live amort + current book value
    'STRANDED_RESIDUAL', // endDate elapsed but book value still > 0 (shortened-modify leftover)
    'ROGUE_GL_LINE', // a POSTED PREPAID_ASSET line with no prepaid-originated JV
]);
export const PrepaidReconExceptionSchema = z.object({
    category: PrepaidReconExceptionCategorySchema,
    contractId: z.uuid().nullable(),
    contractCode: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    detail: z.string(),
    amount: SignedDecimalLike.nullable(),
});
export const PrepaidReconExceptionsResultSchema = z.object({
    period: z.string(),
    exceptions: z.array(PrepaidReconExceptionSchema),
});
//# sourceMappingURL=prepaid.js.map